<template>
  <PageTemplate v-if="isViewReady" heading="Brukerprofil" class="userProfileSettings">
    <!-- Verify User Profile Email Modal -->
    <hl-Modal
      v-if="isVerifyEmailModalShown"
      class="text-center"
      iconClass="budicon-email text-secondary"
      title="Verifisering av e-post"
    >
      <p>For å verifisere din e-post har vi sendt deg en e-post. Følg instruksjonene i e-posten.</p>

      <div class="mt-5">
        <button class="btn btn-outline-secondary mx-2" @click="toggleVerifyEmailModal">OK</button>
      </div>
    </hl-Modal>
    <!-- Verify User Profile Email Modal -->

    <!-- Verify User Profile Phone Modal -->
    <hl-Modal v-if="showVerifyPhone" class="text-center sms-verification">
      <!-- <hl-VerifyPhone
        :phonePrefix="phonePrefix"
        :cellPhone="localCopy.cellPhone"
        @verificationSuccess="verifyPhoneAndCloseModal"
      ></hl-VerifyPhone> -->
      <h2>SMS verifikasjon</h2>
      <p class="mt-3">
        Skriv inn koden som ble sendt som SMS til
        <b class="font-weight-bold">{{ phonePrefixFormatted }} {{ localCopy.cellPhone }}</b
        >.
      </p>
      <hl-Input class="mt-5" v-model="verificationPinCode" placeholder="*****"></hl-Input>
      <hl-Button class="mt-4" variant="primary" @click="verifyPhone" :disabled="disabledVerifyPhone"
        >Verifiser</hl-Button
      >
    </hl-Modal>
    <!-- Verify User Profile Phone Modal -->

    <!-- Update User Info Modal -->
    <hl-Modal
      v-if="isUpdateUserInfoModalShown"
      class="text-center"
      iconClass="budicon-alert-sign text-warning"
      title="Bekreft oppdatering av brukerprofil"
    >
      <p>Vennligst bekreft at du ønsker å oppdatere brukerprofilen din. Hvis ikke klikk “Avbryt”.</p>

      <div class="mt-5">
        <button class="btn btn-outline-secondary mx-2" @click="toggleUpdateUserInfoModal">Avbryt</button>

        <button class="btn btn-primary mx-2" @click="saveUserInfo">Bekreft</button>
      </div>
    </hl-Modal>
    <!-- Update User Info Modal -->

    <InfoCard>
      En brukerprofil er din personlige bruker på Husleie.no. I stedet for å dele brukernavn og passord med andre bør du
      heller gi vedkommende (ektefelle, familiemedlem, kollega, samarbeidspartner etc) tilgang til din(e)
      utleierprofil(er). Dette gjør du under Utleierprofil.
    </InfoCard>

    <div class="userProfileSettings__grid">
      <div class="userProfileSettings__grid__gridRow mt-4">
        <BlockContainer class="p-4">
          <h5>Navn & fødselsdato</h5>

          <hl-Input type="text" label="For- og etternavn" v-model="localCopy.name" class="mt-4" disabled />

          <hl-Input type="text" label="Fødselsdato" v-model="localCopy.dateOfBirth" class="mt-3" disabled />
        </BlockContainer>

        <BlockContainer class="p-4">
          <div class="d-flex justify-content-between align-items-center">
            <h5>BankID-verifisering</h5>
            <Icon
              :name="IconName.InterfaceVerified"
              :size="IconSize.Large"
              :class="[userIdentity ? 'text-primary' : 'text-danger']"
            />
          </div>

          <div class="mt-2">
            <p v-if="userIdentity">Din brukerprofil er verifisert med BankID.</p>

            <div v-else>
              <p>
                Ettersom du forvalter dine leieforhold gjennom oss, er det viktig at vi blir litt bedre kjent med deg.
                Når vi har en sikker identitet på både utleier og leietaker, kan vi skape tryggere leieforhold og tilby
                dere en bedre og sikrere tjeneste.
              </p>

              <p>Trykk på knappen under for å starte prosessen.</p>

              <p v-if="isUserSudo" class="text-danger font-weight-bold">
                Denne funksjonaliteten fungerer ikke via sudo
              </p>

              <hl-Button :href="verificationAppURL" rel="noopener" target="_blank" :disabled="isUserSudo">
                Verifiser
              </hl-Button>
            </div>
          </div>
        </BlockContainer>
      </div>

      <div class="userProfileSettings__grid__gridRow">
        <BlockContainer class="p-4">
          <ValidationObserver ref="userProfileForm" v-slot="{ invalid, dirty }">
            <h5>Kontaktinformasjon</h5>

            <div class="mt-4">
              <hl-Phone
                type="text"
                label="Landkode"
                placeholder="+47"
                v-model="phonePrefixFormatted"
                :disabled="isPhonePrefixDisabled"
              >
                <ValidationProvider
                  name="Telefon"
                  :rules="validationRules.userProfile.userInfo.cellPhone"
                  v-slot="{ errors }"
                >
                  <hl-Input type="tel" label="Telefon" v-model="localCopy.cellPhone" :error="errors[0]" />
                </ValidationProvider>
              </hl-Phone>

              <div class="mt-3">
                <div v-if="isPhoneVerified">
                  <i class="budicon-solid budicon-solid-tick-sign text-primary"></i>
                  <span class="ml-3 font-weight-bold text-secondary">Verifisert nummer</span>
                </div>

                <div v-else>
                  <i class="budicon-solid budicon-solid-block-sign text-danger"></i>
                  <span class="ml-3 font-weight-bold text-secondary">Telefon ikke verifisert</span>
                </div>
              </div>

              <ValidationProvider name="E-post" :rules="validationRules.userProfile.userInfo.email" v-slot="{ errors }">
                <hl-Input
                  class="mt-3"
                  placeholder="ulf.brukersen@husleie.no"
                  label="E-post"
                  v-model="localCopy.email"
                  :error="errors[0]"
                ></hl-Input>
              </ValidationProvider>

              <div class="mt-3">
                <div v-if="isEmailVerified">
                  <i class="budicon-solid budicon-solid-tick-sign text-primary"></i>
                  <span class="ml-3 font-weight-bold text-secondary">Verifisert e-post</span>
                </div>

                <div v-else>
                  <i class="budicon-solid budicon-solid-block-sign text-danger"></i>
                  <span class="ml-3 font-weight-bold text-secondary">E-post ikke verifisert</span>
                </div>
              </div>
            </div>

            <hl-Button
              class="mt-4"
              :disabled="invalid || !dirty || isSavingUserProfile"
              @click="toggleUpdateUserInfoModal"
            >
              <div class="d-flex">
                <hl-LoaderDots v-if="isSavingUserProfile" variant="white" class="mr-2" />
                Lagre endringer
              </div>
            </hl-Button>
          </ValidationObserver>
        </BlockContainer>

        <BlockContainer class="p-4">
          <ValidationObserver v-slot="{ invalid, dirty, reset }">
            <h5>Passord</h5>

            <ValidationProvider
              name="Nåværende passord"
              :rules="validationRules.userProfile.password.oldPassword"
              v-slot="{ errors }"
            >
              <hl-Input
                class="mt-4"
                type="password"
                label="Nåværende passord"
                v-model="oldPassword"
                :error="errors[0]"
              ></hl-Input>
            </ValidationProvider>

            <ValidationProvider
              name="Nytt passord"
              vid="newPassword"
              :rules="validationRules.userProfile.password.newPassword"
              v-slot="{ errors }"
            >
              <hl-Input
                class="mt-3"
                type="password"
                label="Nytt passord"
                v-model="newPassword"
                :error="errors[0]"
              ></hl-Input>
            </ValidationProvider>

            <ValidationProvider
              name="Gjenta nytt passord"
              :rules="validationRules.userProfile.password.repeatNewPassword"
              v-slot="{ errors }"
            >
              <hl-Input
                class="mt-3"
                type="password"
                label="Gjenta nytt passord"
                v-model="newPasswordRepeat"
                :error="errors[0]"
              ></hl-Input>
            </ValidationProvider>

            <hl-Button
              class="mt-4"
              :disabled="invalid || !dirty || isSavingUserPassword"
              @click="saveUserPassword(reset)"
            >
              <div class="d-flex">
                <hl-LoaderDots v-if="isSavingUserPassword" variant="white" class="mr-2" />
                Lagre endringer
              </div>
            </hl-Button>
          </ValidationObserver>
        </BlockContainer>
      </div>
    </div>

    <BlockContainer class="p-4 mt-4">
      <h5>Bytte identitet på din brukerprofil eller slette bruker</h5>

      <p>
        Dersom du ønsker å bytte identitet på din brukerprofil eller slette brukeren så må du ta kontakt med vår
        kundeservice. De vil sørge for at du blir følgt opp og får den hjelpen du trenger.
      </p>

      <hl-Button href="https://www.husleie.no/kontakt-oss/" rel="noopener" target="_blank" class="mt-1">
        Kontakt kunderservice
      </hl-Button>
    </BlockContainer>
  </PageTemplate>
</template>

<script>
import { mask } from 'vue-the-mask';
import addValidationRules from '@/mixins/addValidationRules.js';
import BlockContainer from '@/components/BlockContainer.vue';
import SaveButton from '@/components/UI/buttons/SaveButton';
import { mapGetters, mapActions, mapState } from 'vuex';
import * as moment from 'moment';
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';
import { ProfilesUserApi } from '@modules/husleieapi2';
import PageTemplate from '@/components/PageTemplate.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Phone from '@modules/husleie-framework/src/components/forms/inputs/phone/Phone.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots';
import InfoCard from '@/components/InfoCard.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import Icon, { IconName, IconSize } from '@modules/design-system/components/Icon';
import { UserApi } from '@modules/husleieapi2';

export default {
  mixins: [addValidationRules],

  components: {
    BlockContainer,
    SaveButton,
    PageTemplate,
    InfoCard,
    'hl-Modal': Modal,
    'hl-Input': Input,
    'hl-Phone': Phone,
    'hl-Button': Button,
    'hl-LoaderDots': LoaderDots,
    Icon,
  },

  data() {
    return {
      isViewReady: false,
      disabledVerifyPhone: false,
      localCopy: {
        name: '',
        cellPhone: null,
        dateOfBirth: '',
        email: '',
        newDateOfBirth: null,
        verified: {
          email: false,
          cellPhone: false,
        },
      },
      oldPassword: '',
      phonePrefix: null,
      phoneNumber: null,
      newPassword: '',
      newPasswordRepeat: '',
      verificationPinCode: null,
      showVerifyPhone: false,
      userIdentity: undefined,
      isSavingUserProfile: false,
      isSavingUserPassword: false,
      IconName,
      IconSize,
    };
  },

  directives: { mask },

  computed: {
    ...mapGetters([
      'isVerifyEmailModalShown',
      'isVerifyPhoneModalShown',
      'isUpdateUserInfoModalShown',
      'isUserPhoneVerified',
      'isUserSudo',
    ]),

    ...mapState({
      isPhoneVerified: state => state.userProfile.userProfile.verified.cellPhone,
      isEmailVerified: state => state.userProfile.userProfile.verified.email,
    }),

    phonePrefixFormatted: {
      get() {
        if (this.phonePrefix && this.phonePrefix[0] !== '+') {
          return `+${this.phonePrefix}`;
        } else {
          return this.phonePrefix;
        }
      },
      set(val) {
        this.phonePrefix = val;
      },
    },

    isPhonePrefixDisabled() {
      return this.phonePrefix === '47';
    },

    hasChangedPhone() {
      return this.localCopy.cellPhone !== this.originalCellPhone;
    },

    isUserProfileFormTouched() {
      return this.$store.state.userProfile.userProfile.name !== this.localCopy.name;
    },

    isFormDirty() {
      return true;
    },

    verificationAppURL() {
      return RedirectOperations.getURL(AppRedirect.BANKID_VERIFICATION);
    },

    iconName() {
      return IconName.InterfaceVerified;
    },
    iconSize() {
      return IconSize.Large;
    },
  },

  methods: {
    ...mapActions([
      'toggleVerifyEmailModal',
      'toggleVerifyPhoneModal',
      'toggleUpdateUserInfoModal',
      'setUserProfile',
      'setUserPhoneVerification',
      'loadUserProfile',
    ]),

    saveUserInfo() {
      this.isSavingUserProfile = true;

      const hasChangedPhone = this.hasChangedPhone;
      const localCopyFormat = JSON.parse(JSON.stringify(this.localCopy));

      const newDateOfBirthOrPrevious = localCopyFormat.newDateOfBirth || localCopyFormat.dateOfBirth;
      localCopyFormat.dateOfBirth = newDateOfBirthOrPrevious
        ? moment(newDateOfBirthOrPrevious, 'DD.MM.YYYY').format('YYYY-MM-DD')
        : '';
      // we delete the cellPhone if the phone has not been changed
      if (!hasChangedPhone) {
        delete localCopyFormat.cellPhone;
      } else {
        localCopyFormat.cellPhone = `${this.phonePrefixFormatted}${localCopyFormat.cellPhone}`;
      }

      this.setUserProfile(localCopyFormat)
        .then(() => {
          this.toggleUpdateUserInfoModal();
          this.$toast('Lagret', 'success');
          // after successful save, an sms is triggered. We show verification modal
          if (hasChangedPhone) {
            this.showVerifyPhone = true;
          }
        })
        .catch(err => {
          this.$toast('Kunne ikke lagre brukerprofilen.', 'error');
        })
        .finally(() => {
          this.isSavingUserProfile = false;
        });
    },

    async saveUserPassword(reset) {
      this.isSavingUserPassword = true;

      try {
        const api = new ProfilesUserApi();
        await api.setProfileUserPassword({
          body: {
            currentPassword: this.oldPassword,
            newPassword: this.newPassword,
          },
        });

        this.oldPassword = '';
        this.newPassword = '';
        this.newPasswordRepeat = '';
        reset();

        this.$toast('Lagret', 'success');
      } catch (error) {
        this.$toast('Kunne ikke endre passord.', 'error');
      }

      this.isSavingUserPassword = false;
    },

    async verifyPhone() {
      const api = new ProfilesUserApi();
      try {
        await api.profileUserVerifyCellPhone({ body: { pinCode: this.verificationPinCode } });
        this.$toast('Verifisert', 'success');
        this.disabledVerifyPhone = true;
        this.showVerifyPhone = false;
        this.loadProfile();
      } catch (error) {
        console.log(error);
        this.$toast('Kunne ikke verifisere telefonnummeret', 'error');
      }
    },

    verifyPhoneAndCloseModal(token, code, verifiedPhone) {
      //this.setUserPhoneVerification(true);

      console.log('verification', token, code, verifiedPhone);
    },

    async loadProfile() {
      const resp = await this.loadUserProfile();

      this.localCopy = JSON.parse(JSON.stringify(resp));
      if (resp.cellPhone) {
        const parsedPhoneNumber = parsePhoneNumberFromString(resp.cellPhone, 'NO');

        if (parsedPhoneNumber) {
          this.localCopy.cellPhone = parsedPhoneNumber.nationalNumber;
          this.phonePrefix = parsedPhoneNumber.countryCallingCode;
          this.originalCellPhone = parsedPhoneNumber.nationalNumber;
        }
      }

      if (resp.dateOfBirth != null) {
        this.localCopy.dateOfBirth = moment(resp.dateOfBirth).format('DD.MM.YYYY');
      }
    },

    async fetchUserIdentity() {
      try {
        const api = new UserApi();
        this.userIdentity = await api.getUserIdentity();
      } catch (error) {
        if (error.response.status !== 404) {
          throw new Error('Failed calling `getUserIdentity`');
        }
      }
    },
  },

  async created() {
    await this.loadProfile();
    await this.fetchUserIdentity();

    this.isViewReady = true;
  },

  mounted() {
    if (this.$refs.userProfileForm) {
      this.$refs.userProfileForm.validate();
    }
  },
};
</script>

<style scoped lang="scss">
.userProfileSettings__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem 0rem;
}
.userProfileSettings__grid__gridRow {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
.color-inherit {
  color: inherit;
}

.sms-verification {
  z-index: 1200;
}

@media (min-width: 1250px) {
  .userProfileSettings__grid__gridRow {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
